/**
 * DMS 90-Day Strategy Operator — Sprint 2 Approval screen.
 *
 * Layout (desktop): 2/3 strategy viewer on the left, 1/3 feedback chat on the right.
 * Layout (mobile):  chat sits below strategy.
 *
 * Key behaviours (per STRATEGY_OPERATOR_SPEC.md):
 *  - Every section has a "Regenerate" button (costs 1 credit).
 *  - Generating the initial foundation costs 3 credits.
 *  - Feedback chat MUTATES structured data (not chat). We render the change list
 *    returned by the API as an item-level diff (added / removed / edited) that
 *    highlights which pillars/buckets/etc. moved.
 *  - Approve locks the foundation and returns the user to /strategy home.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { meta } from "@/lib/metaPixel";
import {
  ArrowLeft, Loader2, Sparkles, Send, RotateCw, CheckCircle2, ShieldCheck,
  PlusCircle, MinusCircle, Edit3, Info,
} from "lucide-react";

const SECTION_LABELS = {
  objective: "90-day Objective",
  positioning: "Positioning",
  pillars: "Content Pillars",
  buckets: "Content Buckets",
  channel_role_map: "Channel Roles",
  campaign_arcs: "Campaign Arcs",
  weekly_themes: "Weekly Themes",
  cta_strategy: "CTA Strategy",
  risks_and_notes: "Risks & Notes",
};

const CHANGE_ICONS = {
  added: <PlusCircle className="w-3.5 h-3.5 text-emerald-600" />,
  removed: <MinusCircle className="w-3.5 h-3.5 text-red-600" />,
  edited: <Edit3 className="w-3.5 h-3.5 text-amber-600" />,
};


/**
 * Item-level diff for list sections. Compares by a stable key so we can show
 * exactly which pillar/bucket was added, removed, or edited.
 */
function diffList(before, after, keyFn) {
  const b = before || [];
  const a = after || [];
  const bMap = new Map(b.map((x, i) => [keyFn(x, i), x]));
  const aMap = new Map(a.map((x, i) => [keyFn(x, i), x]));
  const changes = [];
  bMap.forEach((prev, k) => {
    if (!aMap.has(k)) changes.push({ kind: "removed", key: k, prev });
  });
  aMap.forEach((next, k) => {
    if (!bMap.has(k)) {
      changes.push({ kind: "added", key: k, next });
    } else if (JSON.stringify(bMap.get(k)) !== JSON.stringify(next)) {
      changes.push({ kind: "edited", key: k, prev: bMap.get(k), next });
    }
  });
  return changes;
}


export default function StrategyApprove() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [foundation, setFoundation] = useState(null);
  const [prevFoundation, setPrevFoundation] = useState(null); // for the diff highlight
  const [approvalStatus, setApprovalStatus] = useState("not_started");
  const [feedback, setFeedback] = useState([]);
  const [busy, setBusy] = useState(null); // "generate" | "regenerate:pillars" | "feedback" | "approve"
  const [message, setMessage] = useState("");
  const [lastChanges, setLastChanges] = useState([]); // from feedback response
  const chatRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/strategy/foundation");
      setFoundation(data.foundation || null);
      setApprovalStatus(data.approval_status || "not_started");
      setFeedback(data.feedback || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load strategy.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [feedback.length]);

  const canApprove = foundation && approvalStatus !== "strategy_approved";
  const alreadyApproved = approvalStatus === "strategy_approved";

  // Poll a background strategy job until it finishes (done | failed).
  const pollJob = useCallback(async (jobId, { timeoutMs = 240_000, intervalMs = 2500 } = {}) => {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      await new Promise(r => setTimeout(r, intervalMs));
      try {
        const { data: job } = await api.get(`/strategy/jobs/${jobId}`);
        if (job.status === "done") return job;
        if (job.status === "failed") throw new Error(job.error || "Job failed");
      } catch (err) {
        const status = err?.response?.status;
        // Abort immediately if the session died or the user lost access — no
        // point looping until timeout.
        if (status === 401 || status === 403) throw err;
        // 404 during polling can be a transient DB read; retry until timeout.
        if (status && status !== 404) continue;
        throw err;
      }
    }
    throw new Error("Strategy job timed out. Try reloading — your strategy may still complete in the background.");
  }, []);

  const handleGenerate = async () => {
    setBusy("generate");
    try {
      const { data } = await api.post("/strategy/generate");
      const job = await pollJob(data.job_id);
      const newFoundation = job.result?.foundation;
      setPrevFoundation(foundation);
      setFoundation(newFoundation);
      setApprovalStatus("strategy_drafted");
      setLastChanges([]);
      // Meta: DMS_StrategyGenerated fires only on the FIRST successful
      // generation per user. `channels_count` is derived from the returned
      // channel_role_map — no strategy content itself is transmitted.
      if (user?.user_id && newFoundation) {
        meta.strategyGenerated(user.user_id, {
          channels_count: Array.isArray(newFoundation.channel_role_map) ? newFoundation.channel_role_map.length : 0,
          strategy_type: "90_day_foundation",
        });
      }
      toast.success(`Strategy foundation generated (−${data.credits_spent} credits).`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Generation failed.");
    } finally {
      setBusy(null);
    }
  };

  const handleRegenerate = async (section, notes = "") => {
    setBusy(`regenerate:${section}`);
    try {
      const { data } = await api.post("/strategy/regenerate-section", { section, notes });
      const job = await pollJob(data.job_id);
      const newFoundation = job.result?.foundation;
      setPrevFoundation(foundation);
      setFoundation(newFoundation);
      setLastChanges([{ section, kind: "regenerated" }]);
      toast.success(`${SECTION_LABELS[section] || section} regenerated (−${data.credits_spent} credit).`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err?.message || "Regeneration failed.");
    } finally {
      setBusy(null);
    }
  };

  const handleSendMessage = async () => {
    const text = message.trim();
    if (!text) return;
    setMessage("");
    setBusy("feedback");
    // Optimistic append
    const pending = { message: text, assistant_note: "…", changes: [], created_at: new Date().toISOString(), _pending: true };
    setFeedback(prev => [...prev, pending]);
    try {
      const { data } = await api.post("/strategy/feedback", { message: text });
      const job = await pollJob(data.job_id);
      const result = job.result || {};
      const newFoundation = result.foundation || foundation;
      const changes = result.changes || [];
      const assistant_note = result.assistant_note || "";
      setPrevFoundation(foundation);
      setFoundation(newFoundation);
      setLastChanges(changes);
      setFeedback(prev => prev.filter(x => !x._pending).concat({
        message: text,
        assistant_note,
        changes,
        created_at: new Date().toISOString(),
      }));
      if (changes.length > 0) {
        toast.success(`Applied ${changes.length} change${changes.length > 1 ? "s" : ""} (−${data.credits_spent} credit).`);
      } else {
        toast.info(assistant_note || "No changes needed.");
      }
    } catch (err) {
      setFeedback(prev => prev.filter(x => !x._pending));
      toast.error(err?.response?.data?.detail || err?.message || "Feedback failed.");
    } finally {
      setBusy(null);
    }
  };

  const handleApprove = async () => {
    setBusy("approve");
    try {
      const { data } = await api.post("/strategy/approve");
      setApprovalStatus(data.approval_status);
      toast.success("Strategy foundation approved.");
      setTimeout(() => nav("/strategy"), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed.");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="p-10 flex items-center gap-2 text-[#64748B]" data-testid="strategy-approve-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your strategy…
      </div>
    );
  }

  // Empty state — no foundation yet.
  if (!foundation) {
    return (
      <div data-testid="strategy-approve-empty" className="max-w-3xl px-5 sm:px-6 lg:px-10 py-10">
        <Link to="/strategy" className="text-[13.5px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Strategy home
        </Link>
        <div className="dms-label mb-2">Strategy foundation</div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">
          Generate your first 90-day strategy
        </h1>
        <p className="mt-3 text-[#475569] text-[15px] max-w-2xl">
          DMS will read the onboarding you just completed and produce your positioning,
          content pillars, content buckets, channel roles, campaign arcs and weekly
          themes. You can regenerate any section, and the feedback chat below directly
          mutates the structure — it does not just reply.
        </p>
        <div className="mt-6 dms-card p-6 border-2 border-[#4FD1C5]/40 flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-[#0A2342] text-[#4FD1C5] flex items-center justify-center">
            <Sparkles className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <div className="font-heading font-bold text-[#0A2342] text-[16px]">Ready to generate</div>
            <div className="text-[13px] text-[#64748B] mt-1">
              Costs <b>3 Strategy Credits</b> (foundation) + 1 credit per regenerate / feedback message. Takes ~60-120 s — feel free to leave the tab open.
            </div>
            <button
              onClick={handleGenerate}
              disabled={busy === "generate"}
              data-testid="strategy-generate-btn"
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-[#0A2342] text-white px-5 py-2.5 text-[13.5px] font-semibold hover:bg-[#152F5A] disabled:opacity-60"
            >
              {busy === "generate" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {busy === "generate" ? "Generating (this can take up to 2 minutes)…" : "Generate strategy foundation"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="strategy-approve" className="max-w-6xl px-5 sm:px-6 lg:px-10 py-6">
      <Link to="/strategy" className="text-[13.5px] text-[#1D4E89] hover:text-[#0A2342] inline-flex items-center gap-1.5 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to Strategy home
      </Link>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-1">
        <div>
          <div className="dms-label mb-1">Strategy foundation · draft</div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-[#0A2342]">Approve your 90-day strategy</h1>
        </div>
        {alreadyApproved ? (
          <span className="inline-flex items-center gap-1.5 rounded-md bg-emerald-50 text-emerald-800 border border-emerald-200 px-3 py-1.5 text-[12.5px] font-semibold">
            <ShieldCheck className="w-3.5 h-3.5" /> Approved
          </span>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={handleGenerate}
              disabled={!!busy}
              data-testid="strategy-regen-all-btn"
              className="inline-flex items-center gap-2 rounded-md border border-[#E2E8F0] bg-white px-4 py-2 text-[12.5px] font-semibold text-[#334155] hover:border-[#1D4E89] disabled:opacity-60"
            >
              {busy === "generate" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCw className="w-3.5 h-3.5" />}
              Regenerate all (3 credits)
            </button>
            <button
              onClick={handleApprove}
              disabled={!canApprove || !!busy}
              data-testid="strategy-approve-btn"
              className="inline-flex items-center gap-2 rounded-md bg-[#0A2342] text-white px-5 py-2 text-[13px] font-semibold hover:bg-[#152F5A] disabled:opacity-60"
            >
              {busy === "approve" ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Approve strategy
            </button>
          </div>
        )}
      </div>

      {/* Last-change banner */}
      {lastChanges.length > 0 && (
        <div className="mt-4 rounded-md border border-[#4FD1C5]/60 bg-[#F0FDFA] p-3 text-[12.5px] text-[#0A2342]" data-testid="strategy-changes-banner">
          <div className="font-semibold flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> {lastChanges.length} update{lastChanges.length > 1 ? "s" : ""} applied</div>
          <ul className="mt-1 space-y-0.5">
            {lastChanges.map((c, i) => (
              <li key={i} className="ml-4 list-disc">
                <b>{SECTION_LABELS[c.section] || c.section}</b>{c.summary ? ` — ${c.summary}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <StrategySections
            foundation={foundation}
            prev={prevFoundation}
            busy={busy}
            onRegenerate={handleRegenerate}
            locked={alreadyApproved}
          />
        </div>
        <aside className="lg:col-span-1">
          <FeedbackPanel
            feedback={feedback}
            chatRef={chatRef}
            message={message}
            setMessage={setMessage}
            onSend={handleSendMessage}
            busy={busy}
            locked={alreadyApproved}
          />
        </aside>
      </div>
    </div>
  );
}


/* ---------------- Strategy viewer ---------------- */

function StrategySections({ foundation, prev, busy, onRegenerate, locked }) {
  const f = foundation;
  return (
    <>
      <Section
        id="objective"
        section="objective"
        onRegenerate={onRegenerate}
        busy={busy}
        locked={locked}
      >
        <p className="text-[14px] text-[#0A2342] leading-relaxed">{f.objective}</p>
      </Section>

      <Section
        id="positioning"
        section="positioning"
        onRegenerate={onRegenerate}
        busy={busy}
        locked={locked}
      >
        <p className="text-[14px] text-[#334155] leading-relaxed">{f.positioning}</p>
      </Section>

      <Section
        id="pillars"
        section="pillars"
        onRegenerate={onRegenerate}
        busy={busy}
        locked={locked}
        badge={`${(f.pillars || []).length} pillars`}
      >
        <ListDiffContainer diff={diffList(prev?.pillars, f.pillars, (x, i) => x.id || x.name || i)}>
          {(f.pillars || []).map((p) => (
            <PillarCard key={p.id || p.name} pillar={p} />
          ))}
        </ListDiffContainer>
      </Section>

      <Section
        id="buckets"
        section="buckets"
        onRegenerate={onRegenerate}
        busy={busy}
        locked={locked}
        badge={`${(f.buckets || []).length} buckets`}
      >
        <ListDiffContainer diff={diffList(prev?.buckets, f.buckets, (x, i) => x.id || x.name || i)}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {(f.buckets || []).map((b) => <BucketCard key={b.id || b.name} bucket={b} />)}
          </div>
        </ListDiffContainer>
      </Section>

      <Section
        id="channel_role_map"
        section="channel_role_map"
        onRegenerate={onRegenerate}
        busy={busy}
        locked={locked}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(f.channel_role_map || []).map((c) => <ChannelCard key={c.channel} channel={c} />)}
        </div>
      </Section>

      <Section
        id="campaign_arcs"
        section="campaign_arcs"
        onRegenerate={onRegenerate}
        busy={busy}
        locked={locked}
      >
        <div className="space-y-2">
          {(f.campaign_arcs || []).map((a) => <ArcRow key={a.id || a.name} arc={a} />)}
        </div>
      </Section>

      <Section
        id="weekly_themes"
        section="weekly_themes"
        onRegenerate={onRegenerate}
        busy={busy}
        locked={locked}
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[13px]">
          {(f.weekly_themes || []).map((w) => (
            <div key={w.week} className="rounded-md border border-[#E2E8F0] p-2.5" data-testid={`week-${w.week}`}>
              <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-[#64748B]">Week {w.week}</div>
              <div className="font-semibold text-[#0A2342] mt-0.5">{w.theme}</div>
              <div className="text-[#475569] mt-1">{w.narrative}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section
        id="cta_strategy"
        section="cta_strategy"
        onRegenerate={onRegenerate}
        busy={busy}
        locked={locked}
      >
        <div className="text-[13.5px] text-[#0A2342]">
          <div><b>Primary:</b> {f.cta_strategy?.primary}</div>
          {f.cta_strategy?.secondary?.length > 0 && (
            <div className="mt-1"><b>Secondary:</b> {f.cta_strategy.secondary.join(", ")}</div>
          )}
          {f.cta_strategy?.notes && <div className="mt-1 text-[#475569]"><b>Notes:</b> {f.cta_strategy.notes}</div>}
        </div>
      </Section>

      <Section
        id="risks_and_notes"
        section="risks_and_notes"
        onRegenerate={onRegenerate}
        busy={busy}
        locked={locked}
      >
        <ul className="list-disc ml-5 text-[13px] text-[#334155] space-y-1">
          {(f.risks_and_notes || []).map((r, i) => <li key={i}>{r}</li>)}
        </ul>
      </Section>
    </>
  );
}


function Section({ id, section, onRegenerate, busy, locked, children, badge }) {
  const isBusy = busy === `regenerate:${section}`;
  return (
    <div className="dms-card p-4 sm:p-5" data-testid={`strategy-section-${section}`}>
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="font-heading font-bold text-[#0A2342] text-[15px]">{SECTION_LABELS[section]}</div>
          {badge && <span className="text-[11px] rounded-full bg-[#F1F5F9] text-[#475569] px-2 py-0.5">{badge}</span>}
        </div>
        {!locked && (
          <button
            onClick={() => onRegenerate(section)}
            disabled={!!busy}
            data-testid={`strategy-regen-${section}`}
            className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-[#1D4E89] hover:text-[#0A2342] disabled:opacity-50"
            title="Regenerate this section (1 credit)"
          >
            {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}
            {isBusy ? "Regenerating…" : "Regenerate (1)"}
          </button>
        )}
      </div>
      {children}
    </div>
  );
}


function ListDiffContainer({ diff, children }) {
  if (!diff || diff.length === 0) return children;
  return (
    <>
      <div className="mb-3 flex flex-wrap gap-1.5 text-[11.5px]" data-testid="section-diff-chips">
        {diff.map((c, i) => (
          <span key={i} className={
            c.kind === "added" ? "inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-2 py-0.5" :
            c.kind === "removed" ? "inline-flex items-center gap-1 rounded-full bg-red-50 text-red-800 border border-red-200 px-2 py-0.5" :
            "inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-2 py-0.5"
          }>
            {CHANGE_ICONS[c.kind]} {c.kind}: <b className="ml-0.5">{
              (c.next?.name || c.prev?.name || c.key || "").toString().slice(0, 28)
            }</b>
          </span>
        ))}
      </div>
      {children}
    </>
  );
}


function PillarCard({ pillar }) {
  return (
    <div className="rounded-md border border-[#E2E8F0] p-3 mb-2" data-testid={`pillar-${pillar.id || pillar.name}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="font-semibold text-[#0A2342] text-[14px]">{pillar.name}</div>
        <div className="text-[11px] text-[#64748B]">
          {pillar.audience_segment} · {pillar.offer_supported}
        </div>
      </div>
      <div className="text-[12.5px] text-[#475569] mt-1">{pillar.purpose}</div>
      {pillar.example_hooks?.length > 0 && (
        <ul className="mt-2 text-[12.5px] text-[#334155] list-disc ml-5 space-y-0.5">
          {pillar.example_hooks.slice(0, 3).map((h, i) => <li key={i}>{h}</li>)}
        </ul>
      )}
      <div className="mt-2 flex flex-wrap gap-1 text-[11px]">
        {(pillar.channels || []).map(c => (
          <span key={c} className="rounded-full bg-[#0A2342] text-white px-2 py-0.5">{c}</span>
        ))}
        {pillar.cta_type && <span className="rounded-full border border-[#E2E8F0] text-[#334155] px-2 py-0.5">CTA: {pillar.cta_type}</span>}
      </div>
    </div>
  );
}


function BucketCard({ bucket }) {
  return (
    <div className="rounded-md border border-[#E2E8F0] p-3" data-testid={`bucket-${bucket.id || bucket.name}`}>
      <div className="font-semibold text-[#0A2342] text-[13.5px]">{bucket.name}</div>
      <div className="text-[11px] text-[#64748B] mt-0.5">
        {bucket.format} · Pillar: {bucket.pillar_id}
      </div>
      <div className="text-[12px] text-[#475569] mt-1.5">{bucket.example_prompt}</div>
      {bucket.best_cta && <div className="text-[11.5px] text-[#0A2342] mt-1.5"><b>CTA:</b> {bucket.best_cta}</div>}
    </div>
  );
}


function ChannelCard({ channel }) {
  return (
    <div className="rounded-md border border-[#E2E8F0] p-3">
      <div className="flex items-baseline justify-between">
        <div className="font-semibold text-[#0A2342] text-[13.5px]">{channel.channel}</div>
        <div className="text-[11px] text-[#64748B]">{channel.posting_frequency}</div>
      </div>
      <div className="text-[12px] text-[#475569] mt-1">{channel.role}</div>
      {channel.primary_ctas?.length > 0 && (
        <div className="text-[11.5px] text-[#0A2342] mt-1.5"><b>CTAs:</b> {channel.primary_ctas.join(" · ")}</div>
      )}
    </div>
  );
}


function ArcRow({ arc }) {
  return (
    <div className="rounded-md border border-[#E2E8F0] p-3 flex flex-wrap items-baseline justify-between gap-2">
      <div>
        <div className="font-semibold text-[#0A2342] text-[13.5px]">{arc.name}</div>
        <div className="text-[12px] text-[#475569] mt-0.5">{arc.objective}</div>
      </div>
      <div className="text-[11px] text-[#64748B]">
        Months {arc.months?.join(", ") || ""} · Offer: {arc.offer_focus}
      </div>
    </div>
  );
}


/* ---------------- Feedback chat panel ---------------- */

function FeedbackPanel({ feedback, chatRef, message, setMessage, onSend, busy, locked }) {
  return (
    <div className="dms-card p-4 sticky lg:top-6" data-testid="strategy-feedback-panel">
      <div className="font-heading font-bold text-[#0A2342] text-[15px]">Feedback chat</div>
      <div className="text-[11.5px] text-[#64748B] mt-0.5">
        Ask for changes in plain English. DMS mutates the strategy directly.
      </div>

      <div
        ref={chatRef}
        data-testid="strategy-feedback-log"
        className="mt-3 h-[380px] overflow-y-auto rounded-md border border-[#E2E8F0] bg-[#F8FAFC] p-3 space-y-3"
      >
        {feedback.length === 0 && (
          <div className="text-[12.5px] text-[#94A3B8]">
            Try: “Remove the technical diving pillar, we don&apos;t teach that.”
            <br />Or: “Add a bucket that answers common beginner FAQs.”
          </div>
        )}
        {feedback.map((f, i) => (
          <div key={i} className="space-y-1.5">
            <div className="rounded-md bg-[#0A2342] text-white px-3 py-2 text-[12.5px] leading-relaxed" data-testid={`feedback-user-${i}`}>
              {f.message}
            </div>
            <div className="rounded-md bg-white border border-[#E2E8F0] px-3 py-2 text-[12.5px] text-[#0A2342] leading-relaxed" data-testid={`feedback-note-${i}`}>
              {f._pending ? (
                <span className="inline-flex items-center gap-1.5 text-[#64748B]"><Loader2 className="w-3 h-3 animate-spin" /> Applying…</span>
              ) : (
                <>
                  <div>{f.assistant_note || "(no changes)"}</div>
                  {f.changes?.length > 0 && (
                    <ul className="mt-1.5 space-y-0.5">
                      {f.changes.map((c, ci) => (
                        <li key={ci} className="text-[11.5px] text-[#475569] flex items-start gap-1">
                          <Edit3 className="w-3 h-3 mt-0.5 text-amber-600 flex-shrink-0" />
                          <span><b>{SECTION_LABELS[c.section] || c.section}</b> — {c.summary}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-3">
        <textarea
          value={message}
          onChange={e => setMessage(e.target.value)}
          onKeyDown={e => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              onSend();
            }
          }}
          rows={2}
          disabled={locked || busy === "feedback"}
          data-testid="strategy-feedback-input"
          placeholder={locked ? "Strategy approved — chat disabled." : "Ask for a change…"}
          className="dms-input w-full text-[13px]"
        />
        <div className="mt-2 flex items-center justify-between">
          <div className="text-[11px] text-[#94A3B8]">1 credit per message · ⌘/Ctrl + Enter to send</div>
          <button
            onClick={onSend}
            disabled={locked || busy === "feedback" || !message.trim()}
            data-testid="strategy-feedback-send"
            className="inline-flex items-center gap-1.5 rounded-md bg-[#0A2342] text-white px-4 py-1.5 text-[12.5px] font-semibold hover:bg-[#152F5A] disabled:opacity-60"
          >
            {busy === "feedback" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            Send
          </button>
        </div>
      </div>
    </div>
  );
}
